

import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Chip,
  Alert,
  CircularProgress,
  Snackbar,
  IconButton
} from '@mui/material';
import { ArrowBack, LocationOn, CheckCircle, Cancel } from '@mui/icons-material';
import { orderAssignmentAPI } from './deliveryAgentAPI';

const OrderDetails = ({ order, onBack, onOrderUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'info' 
  });

  // Determine what actions are available
  const isPending = order.assignmentStatus === 'pending';
  const isAccepted = order.assignmentStatus === 'accepted';
  const isPickedUp = order.deliveryStatus === 'picked_up' || order.orderStatus === 'sample_collected';
  const isDelivered = order.deliveryStatus === 'delivered' || order.orderStatus === 'completed';
  
  // Check if payment is needed
  const isAlreadyPaid = order.isPaid === true ||
    order.paymentStatus === 'success' ||
    order.paymentStatus === 'paid' ||
    order.paymentMethod === 'online';
  
  const isCODMethod = order.paymentMethod && 
    ['cod', 'cash', 'offline'].includes(order.paymentMethod.toLowerCase());
  
  const needsPayment = isCODMethod && !isAlreadyPaid;
  const formatAddress = (fullAddress) => {
    if (!fullAddress) return null;
  
    const parts = fullAddress.split(',').map(p => p.trim());
  
    return {
      block: parts[0],
      flatNo: parts[1],
      community: parts[2],
      remaining: parts.slice(3).join(', ')
    };
  };
  
  const handleAcceptAssignment = async () => {
    try {
      setLoading(true);
      
      // ✅ Get assignment token from order
      const assignmentToken = order.assignmentToken;
      
      if (!assignmentToken) {
        console.error('❌ Assignment token is missing!');
        console.log('Order data:', JSON.stringify(order, null, 2));
        
        setSnackbar({
          open: true,
          message: 'Assignment token not found. This order may not be properly assigned. Please contact support.',
          severity: 'error'
        });
        setLoading(false);
        return;
      }
  
      console.log('✅ Sending accept request with token:', assignmentToken.substring(0, 20) + '...');
  
      const response = await orderAssignmentAPI.acceptAssignment(assignmentToken);
      
      console.log('✅ Accept response:', response.data);
      
      if (response.data.success) {
        setSnackbar({
          open: true,
          message: 'Assignment accepted! You can now proceed with pickup.',
          severity: 'success'
        });
        
        // Refresh order data
        setTimeout(() => {
          onOrderUpdate();
        }, 1500);
      }
    } catch (error) {
      console.error('❌ Accept assignment error:', error);
      console.error('Error response:', error.response?.data);
      
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || 'Failed to accept assignment',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRejectAssignment = async () => {
    try {
      setLoading(true);
      
      const assignmentToken = order.assignmentToken;
      
      if (!assignmentToken) {
        setSnackbar({
          open: true,
          message: 'Assignment token not found. Please refresh and try again.',
          severity: 'error'
        });
        setLoading(false);
        return;
      }

      const response = await orderAssignmentAPI.rejectAssignment(
        assignmentToken,
        'Not available'
      );
      
      if (response.data.success) {
        setSnackbar({
          open: true,
          message: 'Assignment rejected',
          severity: 'info'
        });
        
        setTimeout(() => {
          onBack();
        }, 1500);
      }
    } catch (error) {
      console.error('Reject assignment error:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to reject assignment',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} mb={3}>
        <IconButton onClick={onBack}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h5" fontWeight={600}>
          Order Details
        </Typography>
      </Stack>

      {/* Assignment Status Alert */}
      {isPending && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={600} gutterBottom>
            New Assignment
          </Typography>
          <Typography variant="body2">
            Please accept this assignment to proceed with {order.orderType === 'booking' ? 'sample collection' : 'delivery'}
          </Typography>
        </Alert>
      )}

      {isAccepted && !isPickedUp && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Assignment accepted! Ready for {order.orderType === 'booking' ? 'sample collection' : 'pickup'}
        </Alert>
      )}

      {/* Order Info */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" fontWeight={600}>
              Order #{order.orderId}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Chip
                label={order.assignmentStatus || 'N/A'}
                color={
                  order.assignmentStatus === 'accepted' 
                    ? 'success' 
                    : order.assignmentStatus === 'pending' 
                    ? 'warning' 
                    : 'default'
                }
                size="small"
              />
              {order.isPaid !== undefined && (
                <Chip
                  label={order.isPaid ? 'PAID' : 'PAYMENT DUE'}
                  color={order.isPaid ? 'success' : 'error'}
                  size="small"
                />
              )}
            </Stack>
          </Stack>

          <Typography variant="body1" gutterBottom>
            <strong>Order Type:</strong> {order.orderType === 'booking' ? 'Sample Collection' : 'Medicine Delivery'}
          </Typography>

          <Typography variant="body1" gutterBottom>
            <strong>Customer:</strong> {order.customerName}
          </Typography>
          
          <Typography variant="body1" gutterBottom>
            <strong>Phone:</strong>{' '}
            <a href={`tel:${order.customerPhone}`} style={{ color: '#007bff', textDecoration: 'none' }}>
              {order.customerPhone}
            </a>
          </Typography>
          
          <Typography variant="body1" gutterBottom>
            <strong>Amount:</strong> ₹{order.totalAmount}
          </Typography>
          
          {order.paymentMethod && (
            <Typography variant="body1" gutterBottom>
              <strong>Payment:</strong> {order.paymentMethod.toUpperCase()}
            </Typography>
          )}

          <Typography variant="body1" gutterBottom>
            <strong>Status:</strong>{' '}
            <Chip 
              label={order.deliveryStatus || order.orderStatus || 'N/A'} 
              size="small" 
              variant="outlined"
            />
          </Typography>

          {/* Service Name for booking orders */}
          {order.orderType === 'booking' && order.serviceName && (
            <Typography variant="body1" gutterBottom>
              <strong>Service:</strong> {order.serviceName}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Items (for medicine orders) */}
      {order.orderType === 'medicine' && order.items && order.items.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} mb={2}>
              Items
            </Typography>
            {order.items.map((item, idx) => (
              <Box key={idx} mb={1}>
                <Typography variant="body2">
                  {item.medicineName} - Qty: {item.quantity} - ₹{item.price}
                </Typography>
              </Box>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Address */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} mb={1}>
            <LocationOn color="primary" />
            <Typography variant="h6" fontWeight={600}>
              {order.orderType === 'booking' ? 'Collection' : 'Delivery'} Address
            </Typography>
          </Stack>
          
          {/* <Typography variant="body2" sx={{ mb: 1 }}>
            {order.customerAddress?.fullAddress || 'Address not available'}
          </Typography> */}
          <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.6 }}>
          {order.customerAddress?.fullAddress ? (() => {
            const address = formatAddress(order.customerAddress.fullAddress);
            if (!address) return 'Address not available';

            return (
              <>
                {address.block && <div><strong>Block:</strong> {address.block}</div>}
                {address.flatNo && <div><strong>Flat No:</strong> {address.flatNo}</div>}
                {address.community && <div><strong>Community:</strong> {address.community}</div>}
                {address.remaining && <div>{address.remaining}</div>}
              </>
            );
          })() : 'Address not available'}
        </Typography>
          {order.customerAddress?.fullAddress && (
            <Button
              variant="outlined"
              size="small"
              sx={{ mt: 1 }}
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                order.customerAddress.fullAddress
              )}`}
              target="_blank"
            >
              Open in Maps
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Stack spacing={2}>
        {/* Step 1: Accept/Reject Assignment */}
        {isPending && (
          <>
            <Button
              variant="contained"
              color="success"
              size="large"
              fullWidth
              onClick={handleAcceptAssignment}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
            >
              {loading ? 'Accepting...' : 'Accept Assignment'}
            </Button>
            
            <Button
              variant="outlined"
              color="error"
              size="large"
              fullWidth
              onClick={handleRejectAssignment}
              disabled={loading}
              startIcon={<Cancel />}
            >
              Reject Assignment
            </Button>
          </>
        )}

        {/* Step 2: Proceed with Pickup/Collection (Only if accepted and not picked up) */}
        {isAccepted && !isPickedUp && (
          <Button
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            onClick={() => onOrderUpdate(
              order.orderType === 'booking' ? 'sampleCollection' : 'pickupConfirmation'
            )}
          >
            {order.orderType === 'booking' 
              ? 'Start Sample Collection' 
              : 'Confirm Pickup'}
          </Button>
        )}

        {/* Step 3: Confirm Delivery/Completion (Only if picked up and payment handled) */}
        {isPickedUp && !isDelivered && (
          <>
            {needsPayment ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  Payment Required
                </Typography>
                <Typography variant="body2">
                  Please complete OTP verification and payment collection before proceeding.
                </Typography>
              </Alert>
            ) : (
              <Button
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                onClick={() => onOrderUpdate(
                  order.orderType === 'booking' ? 'labTransfer' : 'deliveryVerification'
                )}
              >
                {order.orderType === 'booking' ? 'Transfer to Lab' : 'Confirm Delivery'}
              </Button>
            )}
          </>
        )}

        {/* Already Delivered */}
        {isDelivered && (
          <Alert severity="success">
            {order.orderType === 'booking' 
              ? 'Sample collection completed successfully!' 
              : 'Order has been delivered successfully!'}
          </Alert>
        )}
      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OrderDetails;